// @ts-nocheck
// TODO: react-beautiful-dnd package is deprecated and we need to find an alternative package for drag and drop
import React, { type JSX } from 'react';
import { ListSkeleton } from './Skeletons';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { Box, Grid, List, Stack, Typography } from '@mui/material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { handlePrimaryHeading, handleSubHeading } from './utils/helpers';
import { useDefaultConfig, useWidgetConfigHandlers, DraggableListItem } from './';
import { useTranslation } from 'react-i18next';
import { CustomTransComponent } from 'components';
import { useNewCustomTranslations } from 'hooks';

const renderSelectedLabel = (widgetTypeId: string): JSX.Element => (
    <>
        {widgetTypeId === 'schedule' && <CustomTransComponent translationKey={'COMMON:SELECTED_ASSETS'} />}
        {(widgetTypeId === 'load_groups' || widgetTypeId === 'load_group_trends') && (
            <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.SELECTED_LOAD_GROUPS'} />
        )}
    </>
);

const renderSelectedItems = (selectedNodes: any, widgetTypeId: string, keyToTraverse: string): JSX.Element => (
    <Box my={2} className="bg-white">
        <List disablePadding>
            {selectedNodes?.map(
                (item: any, index: number, prev: any) =>
                    prev[index - 1]?.['id'] !== item?.['id'] && (
                        <DraggableListItem
                            item={item}
                            widgetTypeId={widgetTypeId}
                            key={`${item?.[keyToTraverse] ?? item?.id ?? index}`}
                            isDraggable={false}
                        />
                    )
            )}
        </List>
    </Box>
);

const renderDraggableItem = (
    item: any,
    index: number,
    prev: any,
    widgetTypeId: string,
    keyToTraverse: string
): JSX.Element | boolean =>
    prev[index - 1]?.['id'] !== item?.['id'] && (
        <Draggable
            index={index}
            key={`${item?.[keyToTraverse] ?? item?.id ?? index}`}
            draggableId={`${item?.[keyToTraverse] ?? item?.id ?? index}`}
        >
            {(providedItem): any => (
                <DraggableListItem item={item} providedItem={providedItem} widgetTypeId={widgetTypeId} />
            )}
        </Draggable>
    );

const renderDroppableSection = (selectedNodes: any, widgetTypeId: string, keyToTraverse: string): JSX.Element => (
    <Droppable droppableId="selectedNodes">
        {(provided): any => (
            <Box my={2} ref={provided.innerRef} {...provided.droppableProps} className="bg-white">
                <List disablePadding>
                    {selectedNodes?.map((item: any, index: number, prev: any) =>
                        renderDraggableItem(item, index, prev, widgetTypeId, keyToTraverse)
                    )}
                </List>
                {provided.placeholder}
            </Box>
        )}
    </Droppable>
);

const widgetsWithoutSubHeading = ['device_list', 'score'];

const renderHeading = (widgetTypeId: string, payload: any, selectedNodes: any, t: any): JSX.Element => (
    <Stack className={widgetsWithoutSubHeading.includes(widgetTypeId) ? '' : 'border-bottom-1'}>
        <Typography variant="body1" fontWeight={'600'}>
            {handlePrimaryHeading(widgetTypeId, t)}
            {widgetTypeId === 'load_group_trends' && ` (${selectedNodes?.length ?? 0}/10)`}
        </Typography>
        {!widgetsWithoutSubHeading.includes(widgetTypeId) && (
            <Typography mt={3} variant="body1" pb={3} fontWeight={'400'}>
                {handleSubHeading(widgetTypeId, payload, t)}
            </Typography>
        )}
    </Stack>
);

export const GroupAndDevicesList = (props: any): JSX.Element => {
    useNewCustomTranslations('HELPERS_MSGS');
    useNewCustomTranslations('WIDGETS');
    const { t } = useTranslation();
    const widgetTypeId = props?.details?.widgetType?.id;
    const payload = useTypedSelector((state) => state[widgetTypeId === 'schedule' ? 'schedule_widget' : widgetTypeId]);

    const { isLoading: widgetLoader } = useWidgetConfigHandlers();

    const { onDragEnd } = useDefaultConfig(widgetTypeId);
    const widgetListWithDragAndDrop = [
        'group_list',
        'device_list',
        'schedule',
        'upcoming',
        'environmental_stress_monitoring',
        'score',
    ];
    const selectedNodes = payload?.selectedNodes;
    const keyToTraverse = props?.keyToTraverse;
    const isLoadGroups = ['load_groups', 'load_group_trends'].includes(widgetTypeId);

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Grid py={3} px={2} className="bg-content-wrapper height-800 w-49">
                {renderHeading(widgetTypeId, payload, selectedNodes, t)}
                <Stack mt={widgetTypeId === 'device_list' ? 0 : 2} className="max-height-630 overflow-y-scroll">
                    {widgetLoader && <ListSkeleton total={5} primaryVariant="rectangular" />}
                    {!widgetLoader && (
                        <Stack>
                            <Typography
                                ml={2}
                                variant="overline"
                                className="text-secondary"
                                textTransform={isLoadGroups ? 'uppercase' : 'none'}
                            >
                                {renderSelectedLabel(widgetTypeId)}
                            </Typography>

                            {widgetListWithDragAndDrop?.includes(widgetTypeId)
                                ? renderSelectedItems(selectedNodes, widgetTypeId, keyToTraverse)
                                : renderDroppableSection(selectedNodes, widgetTypeId, keyToTraverse)}
                        </Stack>
                    )}
                </Stack>
            </Grid>
        </DragDropContext>
    );
};
